import React, { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Grid,
  Theme,
  makeStyles,
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Snackbar,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';

import FilterIcon from '@material-ui/icons/FilterList';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import GetAppIcon from '@material-ui/icons/GetApp';

import { useFormik } from 'formik';

import { PrimaryActionButton, SecondaryActionButton } from '../../ui/Buttons';
import Filters from './Filters';
import FilterChips from './FilterChips';
import SiteSearch from './SiteSearch';
import Notification from 'src/components/ui/notification';

import {
  PortfolioFilter,
  PortfolioFilterOptions,
  FilterLabels,
  FilterViewType,
  FilterView,
  FilterGroups,
  downloadCSV,
} from '../../../state/contexts/portfolioPage';

import { primaryGreen } from 'src/data/colors';

import { validateFilters } from './validations/filterValidations';
import { useIsSmallScreen } from 'src/helpers/ui';
import { PortfolioQuery } from 'src/api';
import { RootState } from 'src/state';

export interface SiteFilterProps {
  loading?: boolean;
  isAdmin?: boolean;
  hideToggle?: boolean;
  hideChips?: boolean;
  currentFilters: PortfolioFilter;
  filterOptions: PortfolioFilterOptions;
  handleFilterChange: (filter: PortfolioFilter) => void;
  handleSearch: (searchText: string) => void;
  activeSearch?: string;
  query: PortfolioQuery;
}

const getActiveFilterViews = (filters: PortfolioFilter): FilterViewType[] => {
  const activeFilters: FilterViewType[] = [];

  if (filters.endUserName || filters.endUserAddress) {
    activeFilters.push(FilterView.endUserNameAndAddress);
  }

  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      if (key in FilterView) {
        if (!(key === 'equipmentType' && filters.equipmentFirmwareVersion)) {
          activeFilters.push(key as FilterViewType);
        }
      }
    }
  });

  const uniqueFilters = activeFilters.filter(
    (item, index) => activeFilters.indexOf(item) === index
  );
  return uniqueFilters.length > 0 ? uniqueFilters : [];
};

//TODO: test this component
const SiteFilter = (props: SiteFilterProps) => {
  const {
    currentFilters,
    handleFilterChange,
    filterOptions,
    loading,
    handleSearch,
    activeSearch,
    query,
  } = props;

  const classes = useStyles();
  const isSmallScreen = useIsSmallScreen();
  const [open, setOpen] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedFilterViews, setSelectedFilterViews] = useState<FilterViewType[]>(() =>
    getActiveFilterViews(currentFilters)
  );
  const [lastFilterAdded, setLastFilterAdded] = useState<FilterViewType | null>(null);
  const [orderOfFilters, setOrderOfFilters] = useState<FilterViewType[]>(() =>
    getActiveFilterViews(currentFilters)
  );
  const dispatch = useDispatch();
  const isDownloadLoading = useSelector(
    (state: RootState) => state.contexts.portfolioPage.loadingDownload
  );
  const downloadError = useSelector(
    (state: RootState) => state.contexts.portfolioPage.downloadError
  );
  const [showError, setShowError] = useState(false);

  const formik = useFormik<PortfolioFilter>({
    enableReinitialize: true,
    initialValues: props.currentFilters,
    validate: (values) => validateFilters(values, selectedFilterViews),
    onSubmit: (filter: PortfolioFilter) => {
      handleFilterChange(filter);
    },
  });

  const { isValid, dirty, values, initialValues } = formik;
  const hasFormChanged = JSON.stringify(values) !== JSON.stringify(initialValues);
  const isSubmitDisabled = !isValid || !dirty || !hasFormChanged || loading;

  const handleCloseFilterSection = (filterView: FilterViewType) => {
    setSelectedFilterViews((prev) => prev.filter((view) => view !== filterView));
    if (selectedFilterViews.length === 1) {
      setSelectedFilterViews([]);
      setOrderOfFilters([]);
    }
    setLastFilterAdded(null);
  };

  const handleOpenDialog = (view?: FilterViewType) => {
    setSelectedFilterViews(getActiveFilterViews(currentFilters));
    setOpen(true);
    if (view) {
      setLastFilterAdded(view);
    }
  };

  const handleCancel = () => {
    formik.resetForm();
    setOpen(false);
    setLastFilterAdded(null);
  };

  const handleRemoveChip = async (keys: (keyof PortfolioFilter)[], filterView?: FilterViewType) => {
    const fieldsToReset = keys.reduce((acc, key) => {
      const groupFields = Object.values(FilterGroups).find((group: readonly string[]) =>
        group.includes(key)
      );
      return [...acc, ...(groupFields || [key])];
    }, [] as string[]);

    const uniqueFieldsToReset = Array.from(new Set(fieldsToReset));
    const updates = uniqueFieldsToReset.reduce(
      (acc, field) => ({
        ...acc,
        [field]: Array.isArray(formik.values[field as keyof PortfolioFilter]) ? [] : undefined,
      }),
      {}
    );

    await formik.setValues({
      ...formik.values,
      ...updates,
    });

    if (filterView) {
      handleCloseFilterSection(filterView);
    }

    formik.submitForm();
    setLastFilterAdded(null);
    setOrderOfFilters((prev) => prev.filter((view) => view !== filterView));
  };

  const handleAddFilter = (key: keyof PortfolioFilter) => {
    setAnchorEl(null);
    const newFilterView = key as FilterViewType;
    setSelectedFilterViews((prev) => {
      if (prev.length === 0) {
        return [newFilterView];
      }

      // Remove the other filter view if the new one is selected
      // (e.g. if equipmentType is selected, remove equipmentFirmwareVersion)
      if (newFilterView === FilterView.equipmentFirmwareVersion) {
        const index = prev.indexOf(FilterView.equipmentType);
        if (index > -1) {
          prev.splice(index, 1);
        }
      } else if (newFilterView === FilterView.equipmentType) {
        const index = prev.indexOf(FilterView.equipmentFirmwareVersion);

        if (formik.values.equipmentFirmwareVersion) {
          handleRemoveChip([FilterView.equipmentFirmwareVersion]);
        }
        if (index > -1) {
          prev.splice(index, 1);
        }
      }

      return prev.includes(newFilterView) ? prev : [...prev, newFilterView];
    });
    setOrderOfFilters((prev) => {
      if (prev.length === 0) {
        return [newFilterView];
      }

      return prev.includes(newFilterView) ? prev : [...prev, newFilterView];
    });
    setLastFilterAdded(newFilterView);
  };

  const getActiveFiltersCount = (filters: PortfolioFilter): number => {
    const activeGroups = new Set<string>();
    return Object.entries(filters).reduce((count, [key, value]) => {
      if (Array.isArray(value) ? value.length > 0 : value) {
        const group = Object.entries(FilterGroups).find(([, groupKeys]) => groupKeys.includes(key));
        if (group) {
          if (!activeGroups.has(group[0])) {
            activeGroups.add(group[0]);
            return count + 1;
          }
        } else {
          return count + 1;
        }
      }
      return count;
    }, 0);
  };

  const downloadPortfolioCSV = () => {
    dispatch(downloadCSV.request(query));
  };

  useEffect(() => {
    if (downloadError) {
      setShowError(true);
    }
  }, [downloadError]);

  return (
    <>
      <Grid container item spacing={2} alignItems="flex-start" xs={12}>
        <Grid item xs={12} sm="auto">
          <SiteSearch extValue={activeSearch} handleSearch={handleSearch} />
        </Grid>
        <Grid item xs={6} sm="auto">
          <SecondaryActionButton
            classes={{
              root: classes.button,
            }}
            fullWidth={isSmallScreen}
            onClick={() => handleOpenDialog()}
            endIcon={<FilterIcon />}
            data-testid="filter-button"
          >
            {getActiveFiltersCount(currentFilters) > 0 &&
              `${getActiveFiltersCount(currentFilters)} `}
            Filter
          </SecondaryActionButton>
        </Grid>
        {isSmallScreen && (
          <Grid item xs={6}>
            <SecondaryActionButton
              fullWidth={isSmallScreen}
              classes={{
                root: classes.button,
              }}
              onClick={downloadPortfolioCSV}
              endIcon={<GetAppIcon />}
              disabled={isDownloadLoading}
            >
              Download CSV
            </SecondaryActionButton>
          </Grid>
        )}
        <Grid item xs={12} sm style={{ marginTop: '4px' }}>
          <FilterChips
            currentFilters={currentFilters}
            filterOptions={filterOptions}
            onChipRemove={handleRemoveChip}
            onChipClick={handleOpenDialog}
          />
        </Grid>
        {!isSmallScreen && (
          <Grid item sm="auto" style={{ marginLeft: 'auto' }}>
            <SecondaryActionButton
              classes={{
                root: classes.button,
              }}
              onClick={downloadPortfolioCSV}
              endIcon={<GetAppIcon />}
              disabled={isDownloadLoading}
            >
              Download CSV
            </SecondaryActionButton>
          </Grid>
        )}
      </Grid>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        open={showError}
      >
        <Notification severity="error">Something went wrong. Please try again.</Notification>
      </Snackbar>
      <Dialog
        open={open}
        maxWidth="sm"
        classes={{ paper: classes.dialog }}
        onClose={() => setOpen(false)}
      >
        <DialogTitle classes={{ root: classes.dialogTitle }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h5" classes={{ root: classes.h5 }}>
                Filters
              </Typography>
            </Grid>
            <Grid item>
              <SecondaryActionButton
                style={{
                  border: 'none',
                }}
                aria-controls="filter-menu"
                aria-haspopup="true"
                onClick={(event) => {
                  setAnchorEl(event.currentTarget);
                }}
                endIcon={<ArrowDropDownIcon />}
                data-testid="add-filter-button"
              >
                Add Filter
              </SecondaryActionButton>
              <Menu
                id="filter-menu"
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                getContentAnchorEl={null}
                style={{
                  marginTop: 4,
                }}
              >
                {Object.keys(FilterLabels).map((key) => (
                  <MenuItem
                    data-testid={`add-filter-menu-item-${key}`}
                    key={key}
                    onClick={() => handleAddFilter(key as keyof PortfolioFilter)}
                  >
                    {FilterLabels[key as keyof typeof FilterLabels]}
                  </MenuItem>
                ))}
              </Menu>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent classes={{ root: classes.dialogContent }}>
          <Grid container item spacing={2}>
            <Filters
              filterOptions={filterOptions}
              formik={formik}
              selectedFilterViews={selectedFilterViews}
              handleRemoveChip={handleRemoveChip}
              lastFilterAdded={lastFilterAdded}
              setLastFilterAdded={setLastFilterAdded}
              orderOfFilters={orderOfFilters}
            />
          </Grid>
        </DialogContent>
        <DialogActions classes={{ root: classes.dialogActions }}>
          <SecondaryActionButton
            onClick={() => {
              setOrderOfFilters(getActiveFilterViews(currentFilters));
              handleCancel();
            }}
          >
            Cancel
          </SecondaryActionButton>
          <PrimaryActionButton
            onClick={async () => {
              await formik.submitForm();
              handleCancel();
            }}
            disabled={isSubmitDisabled}
            endIcon={
              loading ? <CircularProgress size={16} style={{ color: primaryGreen }} /> : null
            }
          >
            Apply Filters
          </PrimaryActionButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles<Theme>((theme) => ({
  button: {
    backgroundColor: '#FFF',
    height: 40,
  },
  selectMenu: {
    whiteSpace: 'normal',
  },
  dialog: {
    width: 448,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      maxWidth: '100%',
      margin: 16,
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #e0e0e0',
  },
  dialogContent: {
    padding: 24,
  },
  dialogContentPaper: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '8px',
  },
  h5: {
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
  },
  h6: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '0.15px',
  },
  body1: {
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '150%',
  },
  dialogActions: {
    borderTop: '1px solid #e0e0e0',
    padding: 16,
  },
}));

export default SiteFilter;
